import AppHeader from '@Guidelines/Shell/AppShell/AppHeader';
import AppMain from '@Guidelines/Shell/AppShell/AppMain/AppMain';
import SettingsPanel from '@Guidelines/Shell/AppShell/Menu/NavigationMenu/Desktop/SettingsPanel';
import NavigationDrawer from '@Guidelines/Shell/AppShell/Menu/NavigationMenu/Mobile/NavigationDrawer';
import CurrentFacilityProvider from '@UI/Providers/CurrentFacilityProvider';
import CurrentOrganizationProvider from '@UI/Providers/CurrentOrganizationProvider';
import { css } from '@emotion/react';
import type * as React from 'react';

/**
 * Viewport-tall column: the header takes the height it needs, the row below takes the rest. Nothing
 * downstream needs to know the header's height, where subtracting a hard-coded value duplicates it and
 * goes stale on the first change.
 *
 * `dvh`, not `vh`: on mobile `vh` ignores the retractable URL bar and overflows by its height, which
 * this application meets as an installable PWA.
 */
const shellStyle = css`
	display: flex;
	flex-direction: column;
	height: 100dvh;
`;

/** `position: relative` anchors the overlaid settings panel and its mask to the area below the header; a pinned panel is a column of this row, beside `AppMain`, which is offset to stay clear of it. `min-height: 0` lets the row shrink to the column instead of stretching to its content, so each screen scrolls inside `AppMain` rather than the document. */
const bodyStyle = css`
	position: relative;
	display: flex;
	flex: 1;
	min-height: 0;
`;

/**
 * Authenticated shell: top bar (`AppHeader`), navigation (top bar entries and settings panel on wide viewports,
 * drawer on narrow ones) and routed page (`AppMain`). Mounted behind `RequireSession`, so
 * `sessionStatus === 'authenticated'` always pairs with a non-null user.
 *
 * `CurrentOrganizationProvider` sits above `CurrentFacilityProvider`, which reads the current
 * organization's facilities off it instead of fetching them itself — a single `assignments` request
 * feeds the organization context, the facility context and the header switcher's grouping alike.
 */
const AppShell: React.FC = () => {
	return (
		<CurrentOrganizationProvider>
			<CurrentFacilityProvider>
				<div css={shellStyle}>
					<AppHeader />
					<div css={bodyStyle}>
						<SettingsPanel />
						<AppMain />
					</div>
				</div>
				<NavigationDrawer />
			</CurrentFacilityProvider>
		</CurrentOrganizationProvider>
	);
};

export default AppShell;
